import { useMemo, useContext, useCallback } from 'react';
import { useGetTabRecordsQuery } from '@/store/tabsApi';
import { UserContext } from '@/contexts/userContext';
import { HoldingViewModel, HoldingTabConfig } from '@/store/holdingsApi';
import { useLocalization } from '@/hooks/useLocalization';

interface UseHoldingTabsResult {
  tabs: HoldingTabConfig[];
  isLoading: boolean;
  error: any;
}

export const useHoldingTabs = (
  holding: HoldingViewModel | undefined,
  alsoHeldHoldingsData?: any,
): UseHoldingTabsResult => {
  const { userInfo } = useContext(UserContext);
  const { t } = useLocalization();
  const assetAllocationTabName = t('Asset Allocation');

  // Call the tabs API with the correct key matching legacy behavior
  const {
    data: tabRecords,
    isLoading,
    error,
  } = useGetTabRecordsQuery('investor-holding', {
    // Skip the query if no holding is provided
    skip: !holding,
  });

  // Permission checking function
  const hasPermission = useCallback(
    (module: string): boolean => {
      if (!userInfo?.Restrictions) return true;
      return !userInfo.Restrictions.some(
        (r: any) => r.Type === 'Module' && r.Value === module,
      );
    },
    [userInfo],
  );

  const tabs = useMemo(() => {
    if (!holding) return [];

    // Define all possible tabs with their conditions
    const allTabs: (HoldingTabConfig & { isEnabled: boolean })[] = [
      {
        id: 'pd',
        title: assetAllocationTabName,
        isEnabled: true,
      },
      {
        id: 'historical',
        title: 'Historical Values',
        isEnabled: true,
      },
      {
        id: 'taxlots',
        title: t('gainsLosses'),
        isEnabled: holding.HasTaxLots && hasPermission('Gains&Losses'),
      },
      {
        id: 'other',
        title: `Also Held In`,
        isEnabled: !!(holding.CUSIP || holding.Symbol),
      },
    ];

    // If we have tab configuration from the API, use it to filter and order tabs
    if (tabRecords && tabRecords.length > 0) {
      // Create a map of tab configs by TabUIKey for easy lookup
      const tabConfigMap = new Map(
        tabRecords.map((record) => [record.TabUIKey, record]),
      );

      // Filter tabs based on API response and local conditions
      const enabledTabs = allTabs.filter((tab) => {
        const apiConfig = tabConfigMap.get(tab.id);

        // Tab must be enabled in the API response AND meet local conditions
        return apiConfig?.IsEnabled && tab.isEnabled;
      });

      // Sort tabs according to the Order field from API
      enabledTabs.sort((a, b) => {
        const aConfig = tabConfigMap.get(a.id);
        const bConfig = tabConfigMap.get(b.id);
        const aOrder = aConfig?.Order ?? Infinity;
        const bOrder = bConfig?.Order ?? Infinity;
        return aOrder - bOrder;
      });

      // Use labels from API if available
      const finalTabs = enabledTabs.map((tab) => {
        const apiConfig = tabConfigMap.get(tab.id);

        // Special handling for "Also Held In" tab to include count
        if (tab.id === 'other' && alsoHeldHoldingsData?.Records) {
          const count = alsoHeldHoldingsData.Records.length;
          return {
            ...tab,
            title: apiConfig?.Label
              ? `${apiConfig.Label} (${count})`
              : `Also Held In (${count})`,
          };
        }

        if (tab.id === 'pd') return { ...tab, title: assetAllocationTabName };

        return { ...tab, title: apiConfig?.Label || tab.title };
      });

      return finalTabs;
    }

    // Fallback: If no API response, use local logic only (matching legacy configurableTabs behavior)
    const enabledTabs = allTabs.filter((tab) => tab.isEnabled);

    // Add count to "Also Held In" tab
    return enabledTabs.map((tab) => {
      if (tab.id === 'other' && alsoHeldHoldingsData?.Records) {
        const count = alsoHeldHoldingsData.Records.length;
        return {
          ...tab,
          title: `${tab.title} (${count})`,
        };
      }
      return tab;
    });
  }, [
    holding,
    tabRecords,
    hasPermission,
    alsoHeldHoldingsData,
    assetAllocationTabName,
  ]);

  return {
    tabs,
    isLoading,
    error,
  };
};
